import { Search } from 'lucide-react'
import { useState } from 'react'
import { useDebounceValue } from 'usehooks-ts'

import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import { Pagination, Table } from 'slab'
import { convertGrapeMoneyToNumber, formatMoney } from 'utils'
import { formatMonthName, getNextMonthName } from 'utils/billing'
import { useGetEmployersMemberInvoicesEmployerInvoiceId as useGetMemberInvoices } from 'utils/web_api'
import { type Money } from 'utils/web_api.schemas'

import { useInvoice } from './InvoiceContext'
import { MemberInvoiceBreakdownMobile } from './mobile'
import { WhyBilledMoreThanAllowanceModal } from './modals/WhyBilledMoreThanAllowanceModal'

type SortColumn = 'full_name' | 'plan_year'
type SortDirection = 'asc' | 'desc' | false

interface MemberInvoiceBreakdownProps {
  onMemberClick: (memberInvoiceId: string) => void
}

function formatMoneyValue (money: Money | undefined): string {
  const value = convertGrapeMoneyToNumber(money)
  if (value === 0) {
    return '---'
  }
  return formatMoney(value)
}

function getSortParam (sortColumn: SortColumn, sortDirection: SortDirection): string | undefined {
  if (!sortDirection) {
    return undefined
  }
  return sortDirection === 'desc' ? `-${sortColumn}` : sortColumn
}

export function MemberInvoiceBreakdown ({ onMemberClick }: MemberInvoiceBreakdownProps) {
  const { invoice, amounts } = useInvoice()
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm] = useDebounceValue(searchTerm, 300)
  const [sortColumn, setSortColumn] = useState<SortColumn>('full_name')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const [currentPage, setCurrentPage] = useState(0)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const rowsPerPage = 20

  const nextMonthName = getNextMonthName(invoice.period_end_date)
  const monthName = formatMonthName(invoice.period_end_date)
  const showPlanYear = (invoice.plan_years?.length ?? 0) > 1
  const showDoubleBillingColumn = amounts?.has_double_billing ?? false

  const sortParam = getSortParam(sortColumn, sortDirection)

  // Desktop pagination query
  const { data, isLoading } = useGetMemberInvoices(
    invoice.id,
    {
      'page[number]': currentPage + 1,
      'page[size]': rowsPerPage,
      search: debouncedSearchTerm,
      sort: sortParam,
    },
    {
      query: {
        enabled: !!invoice.id,
      },
    }
  )

  const memberInvoices = data?.data ?? []
  const pagination = data?.pagination

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      if (sortDirection === 'asc') {
        setSortDirection('desc')
      } else if (sortDirection === 'desc') {
        setSortDirection(false)
        setSortColumn('full_name')
      } else {
        setSortDirection('asc')
      }
    } else {
      setSortColumn(column)
      setSortDirection('asc')
    }
    setCurrentPage(0)
  }

  const getSortState = (column: SortColumn): SortDirection => {
    return sortColumn === column ? sortDirection : false
  }

  const handleSearchChange = (value: string) => {
    setSearchTerm(value)
    setCurrentPage(0)
  }

  return (
    <div className='py-6 px-4'>
      <h4 className='body-bold'>Member healthcare costs</h4>

      <div className='mt-6'>
        <div className='relative'>
          <div className='absolute inset-y-0 left-0 flex items-center pl-3'>
            <Search size={16} className='text-gray-400' />
          </div>
          <input
            placeholder='Find a member'
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            className='w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-thatch-500 focus:border-thatch-500'
          />
        </div>
      </div>

      {amounts?.has_premiums_exceeding_allowances && (
        <div className='mt-3'>
          <button
            type='button'
            onClick={() => setIsModalOpen(true)}
            className='text-sm text-thatch-800 underline'
          >
            Why am I being billed for more than employees' allowances?
          </button>
        </div>
      )}

      {isLoading && memberInvoices.length === 0 && (
        <div className='mt-6'>
          <LoadingSpinnerCentered />
        </div>
      )}

      {!isLoading && memberInvoices.length === 0 && (
        <div className='mt-4 text-center text-gray-500'>
          No member invoices found
        </div>
      )}

      {!isLoading && memberInvoices.length > 0 && (
        <>
          {/** Table for desktop */}
          <div className='mt-4 hidden md:block'>
            <Table>
              <Table.Head>
                <Table.Row>
                  <Table.ColumnHeader
                    onSort={() => handleSort('full_name')}
                    sorted={getSortState('full_name')}
                  >
                    Full name
                  </Table.ColumnHeader>
                  {showPlanYear && (
                    <Table.ColumnHeader
                      onSort={() => handleSort('plan_year')}
                      sorted={getSortState('plan_year')}
                    >
                      Plan Year
                    </Table.ColumnHeader>
                  )}
                  <Table.ColumnHeader
                    align='right'
                  >
                    Pre-payment for {nextMonthName} insurance coverage
                  </Table.ColumnHeader>
                  {showDoubleBillingColumn && (
                    <Table.ColumnHeader
                      align='right'
                    >
                      Payment for {monthName} insurance coverage
                    </Table.ColumnHeader>
                  )}
                  <Table.ColumnHeader
                    align='right'
                  >
                    Changes in {monthName} insurance coverage
                  </Table.ColumnHeader>
                  <Table.ColumnHeader
                    align='right'
                  >
                    Qualified medical expenses
                  </Table.ColumnHeader>
                  <Table.ColumnHeader
                    align='right'
                  >
                    Total healthcare cost
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {memberInvoices.map((memberInvoice) => (
                  <Table.Row
                    key={memberInvoice.id}
                    onClick={() => onMemberClick(memberInvoice.id)}
                    accessibleLabel={`View details for ${memberInvoice.full_name || 'member'}`}
                  >
                    <Table.Cell>
                      <span className='whitespace-nowrap'>
                        {memberInvoice.full_name || '---'}
                      </span>
                    </Table.Cell>
                    {showPlanYear && (
                      <Table.Cell>
                        <span className='inline-flex items-start'>
                          {memberInvoice.plan_year ?? '---'}
                        </span>
                      </Table.Cell>
                    )}
                    <Table.Cell align='right'>
                      <span className='inline-flex items-center justify-end'>
                        {formatMoneyValue(memberInvoice.amounts?.total_insurance_premiums)}
                        <span className='ml-1 w-4' aria-hidden />
                      </span>
                    </Table.Cell>
                    {showDoubleBillingColumn && (
                      <Table.Cell align='right'>
                        <span className='inline-flex items-center justify-end'>
                          {formatMoneyValue(memberInvoice.amounts?.double_billing_total)}
                          <span className='ml-1 w-4' aria-hidden />
                        </span>
                      </Table.Cell>
                    )}
                    <Table.Cell align='right'>
                      <span className='inline-flex items-center justify-end'>
                        {formatMoneyValue(
                          memberInvoice.amounts?.coverage_corrections_excluding_double_billing ||
                          memberInvoice.amounts?.coverage_corrections
                        )}
                        <span className='ml-1 w-4' aria-hidden />
                      </span>
                    </Table.Cell>
                    <Table.Cell align='right'>
                      <span className='inline-flex items-center justify-end'>
                        {formatMoneyValue(memberInvoice.amounts?.other)}
                        <span className='ml-1 w-4' aria-hidden />
                      </span>
                    </Table.Cell>
                    <Table.Cell align='right'>
                      <span className='inline-flex items-center justify-end'>
                        {formatMoneyValue(memberInvoice.amounts?.total)}
                        <span className='ml-1 w-4' aria-hidden />
                      </span>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>

          {/** Vertical layout for mobile */}
          <MemberInvoiceBreakdownMobile
            memberInvoices={memberInvoices}
            priorMonthName={monthName}
            nextMonthName={nextMonthName}
            formatMoneyValue={formatMoneyValue}
            showPlanYear={showPlanYear}
            onMemberClick={onMemberClick}
            hasDoubleBilling={amounts?.has_double_billing ?? false}
          />

          {pagination && pagination.total_pages > 1 && (
            <div className='mt-4 flex justify-end'>
              <Pagination
                pageIndex={currentPage}
                perPage={rowsPerPage}
                totalRows={pagination.total_records}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </>
      )}
      <WhyBilledMoreThanAllowanceModal open={isModalOpen} onOpenChange={setIsModalOpen} />
    </div>
  )
}
